import { useEffect, useMemo, useState, type FormEvent } from 'react'
import AdminShell from './AdminShell'
import {
  AdminButton,
  AdminCard,
  AdminConfirmDialog,
  AdminDataTable,
  AdminField,
  AdminFilterBar,
  AdminInput,
  AdminModal,
  AdminPagination,
  AdminSelect,
  AdminSkeleton,
  AdminStatePanel,
  AdminStatusPill,
  AdminToastRegion,
  type AdminTableColumn,
  type AdminToast,
} from './AdminUi'
import '../styles/admin-reviews.css'

type SessionPayload = {
  authenticated: true
  user: { email: string; role: string }
  csrfToken: string
}

type ReviewSource = 'Google' | 'Facebook' | 'Message' | 'Email' | 'Other'

type Review = {
  id: string
  customerDisplayName: string
  reviewText: string
  source: ReviewSource
  rating: number | null
  reviewDate: string | null
  sourceUrl: string | null
  privateAdminNote: string | null
  permissionConfirmed: boolean
  published: boolean
  homepageFeatured: boolean
  homepageOrder: number | null
  version: number
  createdAt: string
  updatedAt: string
}

type ListPayload = {
  reviews: Review[]
  total: number
  page: number
  pageSize: number
  totalPages: number
  homepageSelectedCount: number
  homepageSelection: Review[]
}

type Draft = {
  customerDisplayName: string
  reviewText: string
  source: ReviewSource
  rating: string
  reviewDate: string
  sourceUrl: string
  privateAdminNote: string
  permissionConfirmed: boolean
  published: boolean
  homepageFeatured: boolean
  homepageOrder: string
}

const SOURCES: ReviewSource[] = ['Google', 'Facebook', 'Message', 'Email', 'Other']
const DATE = new Intl.DateTimeFormat('en-NZ', {
  timeZone: 'Pacific/Auckland',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
})

function environmentFromHostname(hostname: string) {
  const host = hostname.toLowerCase()
  if (host.includes('staging')) return 'Staging'
  if (host === 'localhost' || host === '127.0.0.1') return 'Local'
  return undefined
}

function formatDate(value: string | null) {
  if (!value) return '—'
  const parsed = new Date(`${value}T12:00:00.000Z`)
  return Number.isNaN(parsed.getTime()) ? '—' : DATE.format(parsed)
}

function emptyDraft(): Draft {
  return {
    customerDisplayName: '',
    reviewText: '',
    source: 'Google',
    rating: '',
    reviewDate: '',
    sourceUrl: '',
    privateAdminNote: '',
    permissionConfirmed: false,
    published: false,
    homepageFeatured: false,
    homepageOrder: '',
  }
}

function draftFromReview(review: Review): Draft {
  return {
    customerDisplayName: review.customerDisplayName,
    reviewText: review.reviewText,
    source: review.source,
    rating: review.rating === null ? '' : String(review.rating),
    reviewDate: review.reviewDate ?? '',
    sourceUrl: review.sourceUrl ?? '',
    privateAdminNote: review.privateAdminNote ?? '',
    permissionConfirmed: review.permissionConfirmed,
    published: review.published,
    homepageFeatured: review.homepageFeatured,
    homepageOrder: review.homepageOrder === null ? '' : String(review.homepageOrder),
  }
}

function payloadFromDraft(draft: Draft) {
  const rating = draft.rating === '' ? null : Number(draft.rating)
  const homepageOrder = draft.homepageOrder === '' ? null : Number(draft.homepageOrder)
  return {
    customerDisplayName: draft.customerDisplayName,
    reviewText: draft.reviewText,
    source: draft.source,
    rating,
    reviewDate: draft.reviewDate || null,
    sourceUrl: draft.sourceUrl || null,
    privateAdminNote: draft.privateAdminNote || null,
    permissionConfirmed: draft.permissionConfirmed,
    published: draft.published,
    homepageFeatured: draft.homepageFeatured,
    homepageOrder,
  }
}

function sourceNeedsPermission(source: ReviewSource) {
  return source === 'Message' || source === 'Email' || source === 'Other'
}

function redirectForExpiredSession(response: Response) {
  if (response.status !== 401) return false
  window.location.replace('/admin/login?next=/admin/reviews')
  return true
}

function ratingLabel(rating: number | null) {
  return rating === null ? 'No rating' : `${rating}/5`
}

function status(review: Review) {
  if (review.homepageFeatured) return 'Featured'
  return review.published ? 'Published' : 'Draft'
}

export default function AdminReviewsPage() {
  const [session, setSession] = useState<SessionPayload | null>(null)
  const [data, setData] = useState<ListPayload | null>(null)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [source, setSource] = useState('all')
  const [publishStatus, setPublishStatus] = useState('all')
  const [page, setPage] = useState(1)
  const [busy, setBusy] = useState(false)
  const [logoutBusy, setLogoutBusy] = useState(false)
  const [error, setError] = useState('')
  const [editorOpen, setEditorOpen] = useState(false)
  const [editing, setEditing] = useState<Review | null>(null)
  const [draft, setDraft] = useState<Draft>(() => emptyDraft())
  const [deleteTarget, setDeleteTarget] = useState<Review | null>(null)
  const [toasts, setToasts] = useState<AdminToast[]>([])
  const environmentLabel = useMemo(() => environmentFromHostname(window.location.hostname), [])

  function toast(title: string, message?: string) {
    const id = crypto.randomUUID()
    setToasts([{ id, title, message, tone: 'success' }])
    window.setTimeout(() => setToasts((current) => current.filter((item) => item.id !== id)), 4_000)
  }

  async function load(targetPage = page, targetSearch = search, targetStatus = publishStatus, targetSource = source) {
    setBusy(true)
    setError('')
    try {
      const params = new URLSearchParams({
        page: String(targetPage),
        status: targetStatus,
        source: targetSource,
      })
      if (targetSearch) params.set('search', targetSearch)
      const [sessionResponse, listResponse] = await Promise.all([
        fetch('/api/admin/session', { credentials: 'same-origin', cache: 'no-store' }),
        fetch(`/api/admin/reviews?${params}`, { credentials: 'same-origin', cache: 'no-store' }),
      ])
      if (sessionResponse.status === 401 || listResponse.status === 401) {
        window.location.replace('/admin/login?next=/admin/reviews')
        return
      }
      if (sessionResponse.status === 403 || listResponse.status === 403) throw new Error('Owner access is required.')
      if (!sessionResponse.ok || !listResponse.ok) throw new Error('Reviews could not be loaded.')
      setSession(await sessionResponse.json() as SessionPayload)
      const payload = await listResponse.json() as ListPayload
      setData(payload)
      if (payload.page !== targetPage) setPage(payload.page)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Reviews could not be loaded.')
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => {
    void load(page, search, publishStatus, source)
  }, [page, search, publishStatus, source])

  async function logout() {
    if (!session || logoutBusy) return
    setLogoutBusy(true)
    try {
      const response = await fetch('/api/admin/logout', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-CSRF-Token': session.csrfToken },
      })
      if (!response.ok) throw new Error('Logout failed.')
      window.location.replace('/admin/login')
    } catch {
      setError('Logout could not be completed.')
      setLogoutBusy(false)
    }
  }

  function submitSearch(event: FormEvent) {
    event.preventDefault()
    setPage(1)
    setSearch(searchInput.trim())
  }

  function openCreate() {
    setEditing(null)
    setDraft(emptyDraft())
    setError('')
    setEditorOpen(true)
  }

  function openEdit(review: Review) {
    setEditing(review)
    setDraft(draftFromReview(review))
    setError('')
    setEditorOpen(true)
  }

  function validateDraft() {
    if (!draft.customerDisplayName.trim()) return 'Customer display name is required.'
    if (!draft.reviewText.trim()) return 'Review text is required.'
    if (draft.rating && !['1', '2', '3', '4', '5'].includes(draft.rating)) return 'Rating must be blank or 1 to 5.'
    if (draft.published && sourceNeedsPermission(draft.source) && !draft.permissionConfirmed) {
      return 'Confirm permission before publishing Message, Email or Other reviews.'
    }
    if (draft.homepageFeatured && !draft.published) return 'Only published reviews can be featured.'
    if (draft.homepageFeatured && !['1', '2', '3'].includes(draft.homepageOrder)) return 'Choose homepage position 1, 2 or 3.'
    return ''
  }

  async function saveEditor() {
    if (!session || busy) return
    const validation = validateDraft()
    if (validation) {
      setError(validation)
      return
    }

    setBusy(true)
    setError('')
    try {
      const response = await fetch(editing ? `/api/admin/reviews/${encodeURIComponent(editing.id)}` : '/api/admin/reviews', {
        method: editing ? 'PUT' : 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': session.csrfToken,
        },
        body: JSON.stringify(editing
          ? { ...payloadFromDraft(draft), expectedVersion: editing.version }
          : payloadFromDraft(draft)),
      })
      const result = await response.json().catch(() => null) as { review?: Review; message?: string } | null
      if (redirectForExpiredSession(response)) return
      if (!response.ok) throw new Error(result?.message || 'Review could not be saved.')
      setEditorOpen(false)
      setEditing(null)
      toast(editing ? 'Review updated' : 'Review added')
      await load(1, search, publishStatus, source)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Review could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  async function setPublish(review: Review, publish: boolean) {
    if (!session || busy) return
    setBusy(true)
    setError('')
    try {
      const response = await fetch(`/api/admin/reviews/${encodeURIComponent(review.id)}/${publish ? 'publish' : 'unpublish'}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': session.csrfToken,
        },
        body: JSON.stringify({ expectedVersion: review.version }),
      })
      const result = await response.json().catch(() => null) as { message?: string } | null
      if (redirectForExpiredSession(response)) return
      if (!response.ok) throw new Error(result?.message || 'Review visibility could not be changed.')
      toast(publish ? 'Review published' : 'Review unpublished', publish ? 'It is now eligible for the public Reviews page.' : 'It has been removed from public review surfaces.')
      await load(page, search, publishStatus, source)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Review visibility could not be changed.')
    } finally {
      setBusy(false)
    }
  }

  async function unfeature(review: Review) {
    if (!session || busy) return
    setBusy(true)
    setError('')
    try {
      const response = await fetch(`/api/admin/reviews/${encodeURIComponent(review.id)}/unfeature`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': session.csrfToken,
        },
        body: JSON.stringify({ expectedVersion: review.version }),
      })
      const result = await response.json().catch(() => null) as { message?: string } | null
      if (redirectForExpiredSession(response)) return
      if (!response.ok) throw new Error(result?.message || 'Homepage selection could not be changed.')
      toast('Review removed from homepage')
      await load(page, search, publishStatus, source)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Homepage selection could not be changed.')
    } finally {
      setBusy(false)
    }
  }

  async function reorderHomepage(nextSelection: Review[]) {
    if (!session || busy) return
    setBusy(true)
    setError('')
    try {
      const response = await fetch('/api/admin/reviews/homepage-order', {
        method: 'PUT',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': session.csrfToken,
        },
        body: JSON.stringify({
          items: nextSelection.map((review) => ({ id: review.id, expectedVersion: review.version })),
        }),
      })
      const result = await response.json().catch(() => null) as { message?: string } | null
      if (redirectForExpiredSession(response)) return
      if (!response.ok) throw new Error(result?.message || 'Homepage order could not be saved.')
      toast('Homepage review order updated')
      await load(page, search, publishStatus, source)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Homepage order could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  async function deleteReview() {
    if (!session || !deleteTarget || busy) return
    const target = deleteTarget
    setBusy(true)
    setError('')
    try {
      const response = await fetch(`/api/admin/reviews/${encodeURIComponent(target.id)}`, {
        method: 'DELETE',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': session.csrfToken,
        },
        body: JSON.stringify({ expectedVersion: target.version }),
      })
      const result = await response.json().catch(() => null) as { message?: string } | null
      if (redirectForExpiredSession(response)) return
      if (!response.ok) throw new Error(result?.message || 'Review could not be deleted.')
      setDeleteTarget(null)
      toast('Review deleted')
      await load(page, search, publishStatus, source)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Review could not be deleted.')
    } finally {
      setBusy(false)
    }
  }

  const columns: AdminTableColumn<Review>[] = [
    {
      key: 'customer',
      label: 'Customer',
      width: '14%',
      render: (row) => <button className="admin-reviews__customer-link" type="button" onClick={() => openEdit(row)}>{row.customerDisplayName}</button>,
    },
    { key: 'source', label: 'Source', render: (row) => <span>{row.source}</span> },
    { key: 'rating', label: 'Rating', render: (row) => <span>{ratingLabel(row.rating)}</span> },
    {
      key: 'published',
      label: 'Reviews Page',
      render: (row) => (
        <div className="admin-reviews__cell-stack">
          <AdminStatusPill tone={row.published ? 'success' : 'neutral'}>{row.published ? 'Visible' : 'Hidden'}</AdminStatusPill>
          <button type="button" onClick={() => void setPublish(row, !row.published)} disabled={busy}>
            {row.published ? 'Unpublish' : 'Publish'}
          </button>
        </div>
      ),
    },
    {
      key: 'featured',
      label: 'Homepage Featured',
      render: (row) => <AdminStatusPill tone={row.homepageFeatured ? 'pink' : 'neutral'}>{row.homepageFeatured ? 'Yes' : 'No'}</AdminStatusPill>,
    },
    { key: 'order', label: 'Homepage Order', render: (row) => <span>{row.homepageOrder ?? '—'}</span> },
    { key: 'date', label: 'Review Date', render: (row) => <span>{formatDate(row.reviewDate)}</span> },
    {
      key: 'status',
      label: 'Status',
      render: (row) => <AdminStatusPill tone={row.homepageFeatured ? 'pink' : row.published ? 'success' : 'neutral'}>{status(row)}</AdminStatusPill>,
    },
    { key: 'edit', label: 'Edit', render: (row) => <AdminButton compact variant="secondary" type="button" onClick={() => openEdit(row)}>Edit</AdminButton> },
    { key: 'delete', label: 'Delete', align: 'right', render: (row) => <AdminButton compact variant="danger" type="button" onClick={() => setDeleteTarget(row)}>Delete</AdminButton> },
  ]

  if (error && (!session || !data)) {
    return (
      <main className="admin-shell-gate-error">
        <AdminStatePanel state="error" title="Reviews unavailable" description={error} action={<AdminButton onClick={() => void load()}>Retry</AdminButton>} />
      </main>
    )
  }

  if (!session || !data) {
    return <main className="admin-shell-gate-loading"><AdminCard><AdminSkeleton lines={8} /></AdminCard></main>
  }

  const homepageSelection = [...data.homepageSelection].sort((a, b) => (a.homepageOrder ?? 99) - (b.homepageOrder ?? 99))

  return (
    <AdminShell
      title="Reviews"
      eyebrow="CONTENT & COMMS"
      description="Manage real customer reviews for the Reviews page and homepage."
      user={session.user}
      onLogout={() => void logout()}
      logoutBusy={logoutBusy}
      environmentLabel={environmentLabel}
      actions={<AdminButton type="button" onClick={openCreate}>Add Review</AdminButton>}
    >
      <div className="admin-reviews-page">
        {error ? <div className="admin-reviews__error" role="alert">{error}</div> : null}

        <AdminCard className="admin-reviews__homepage">
          <div className="admin-reviews__homepage-heading">
            <div>
              <span>HOMEPAGE</span>
              <h2>Featured reviews</h2>
              <p>Homepage section is shown only when exactly 3 published reviews are selected.</p>
            </div>
            <strong>{data.homepageSelectedCount} / 3 selected</strong>
          </div>
          {homepageSelection.length ? (
            <ol className="admin-reviews__homepage-list">
              {homepageSelection.map((review, index) => (
                <li key={review.id}>
                  <span className="admin-reviews__slot">{index + 1}</span>
                  <div>
                    <strong>{review.customerDisplayName}</strong>
                    <small>{review.source} · {ratingLabel(review.rating)}</small>
                  </div>
                  <div className="admin-reviews__homepage-actions">
                    <button
                      type="button"
                      aria-label={`Move ${review.customerDisplayName} up`}
                      disabled={busy || index === 0}
                      onClick={() => {
                        const next = [...homepageSelection]
                        ;[next[index - 1], next[index]] = [next[index], next[index - 1]]
                        void reorderHomepage(next)
                      }}
                    >↑</button>
                    <button
                      type="button"
                      aria-label={`Move ${review.customerDisplayName} down`}
                      disabled={busy || index === homepageSelection.length - 1}
                      onClick={() => {
                        const next = [...homepageSelection]
                        ;[next[index], next[index + 1]] = [next[index + 1], next[index]]
                        void reorderHomepage(next)
                      }}
                    >↓</button>
                    <button type="button" disabled={busy} onClick={() => void unfeature(review)}>Remove</button>
                  </div>
                </li>
              ))}
            </ol>
          ) : (
            <p className="admin-reviews__homepage-empty">No homepage reviews selected yet.</p>
          )}
        </AdminCard>

        <AdminCard padding="none" className="admin-reviews__directory">
          <div className="admin-reviews__card-heading">
            <div>
              <h2>Customer reviews</h2>
              <p>{data.total} {data.total === 1 ? 'review' : 'reviews'} matching the current filters</p>
            </div>
          </div>
          <form onSubmit={submitSearch}>
            <AdminFilterBar actions={(
              <>
                <AdminButton type="submit">Search</AdminButton>
                {(search || source !== 'all' || publishStatus !== 'all') ? (
                  <AdminButton
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setSearchInput('')
                      setSearch('')
                      setSource('all')
                      setPublishStatus('all')
                      setPage(1)
                    }}
                  >
                    Clear
                  </AdminButton>
                ) : null}
              </>
            )}>
              <AdminField label="Search">
                <AdminInput
                  type="search"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Customer or review text"
                />
              </AdminField>
              <AdminField label="Status">
                <AdminSelect value={publishStatus} onChange={(event) => { setPublishStatus(event.target.value); setPage(1) }}>
                  <option value="all">All statuses</option>
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </AdminSelect>
              </AdminField>
              <AdminField label="Source">
                <AdminSelect value={source} onChange={(event) => { setSource(event.target.value); setPage(1) }}>
                  <option value="all">All sources</option>
                  {SOURCES.map((item) => <option value={item} key={item}>{item}</option>)}
                </AdminSelect>
              </AdminField>
            </AdminFilterBar>
          </form>

          {data.reviews.length ? (
            <AdminDataTable
              columns={columns}
              rows={data.reviews}
              rowKey={(row) => row.id}
              caption="Sugar Gremlin customer reviews"
            />
          ) : (
            <AdminStatePanel
              state="empty"
              title="No reviews found"
              description="Add a real customer review or adjust the current filters."
              action={<AdminButton type="button" onClick={openCreate}>Add Review</AdminButton>}
            />
          )}

          {data.totalPages > 1 ? (
            <div className="admin-reviews__pagination">
              <AdminPagination page={data.page} totalPages={data.totalPages} onPageChange={setPage} />
            </div>
          ) : null}
        </AdminCard>

        <AdminModal
          open={editorOpen}
          title={editing ? 'Edit review' : 'Add Review'}
          description="Only add genuine customer feedback. Review text is published exactly as entered."
          onClose={() => { if (!busy) setEditorOpen(false) }}
          footer={(
            <>
              <AdminButton variant="secondary" type="button" onClick={() => setEditorOpen(false)} disabled={busy}>Cancel</AdminButton>
              <AdminButton type="button" onClick={() => void saveEditor()} disabled={busy}>{busy ? 'Saving…' : 'Save Review'}</AdminButton>
            </>
          )}
        >
          <div className="admin-reviews__editor">
            <section className="admin-reviews__editor-section" aria-labelledby="admin-review-section-review">
              <div className="admin-reviews__section-heading">
                <div>
                  <span>SECTION 1</span>
                  <h3 id="admin-review-section-review">Review</h3>
                </div>
              </div>

              <AdminField label="Customer display name" required>
                <AdminInput
                  value={draft.customerDisplayName}
                  maxLength={80}
                  onChange={(event) => setDraft((current) => ({ ...current, customerDisplayName: event.target.value }))}
                />
              </AdminField>

              <AdminField label="Review text" required>
                <textarea
                  className="admin-ui-input admin-reviews__textarea"
                  value={draft.reviewText}
                  maxLength={2000}
                  rows={5}
                  onChange={(event) => setDraft((current) => ({ ...current, reviewText: event.target.value }))}
                />
              </AdminField>

              <div className="admin-reviews__primary-meta">
                <AdminField label="Source" required>
                  <AdminSelect
                    value={draft.source}
                    onChange={(event) => setDraft((current) => ({ ...current, source: event.target.value as ReviewSource }))}
                  >
                    {SOURCES.map((item) => <option value={item} key={item}>{item}</option>)}
                  </AdminSelect>
                </AdminField>

                <AdminField label="Rating" hint="Only add a rating if the original review included one.">
                  <AdminSelect value={draft.rating} onChange={(event) => setDraft((current) => ({ ...current, rating: event.target.value }))}>
                    <option value="">No rating</option>
                    <option value="1">1 / 5</option>
                    <option value="2">2 / 5</option>
                    <option value="3">3 / 5</option>
                    <option value="4">4 / 5</option>
                    <option value="5">5 / 5</option>
                  </AdminSelect>
                </AdminField>

                <AdminField label="Review date">
                  <AdminInput
                    type="date"
                    value={draft.reviewDate}
                    onChange={(event) => setDraft((current) => ({ ...current, reviewDate: event.target.value }))}
                  />
                </AdminField>
              </div>
            </section>

            <section className="admin-reviews__editor-section" aria-labelledby="admin-review-section-source">
              <div className="admin-reviews__section-heading">
                <div>
                  <span>SECTION 2</span>
                  <h3 id="admin-review-section-source">Source details</h3>
                </div>
              </div>

              <AdminField label="Source URL">
                <AdminInput
                  type="url"
                  value={draft.sourceUrl}
                  maxLength={2048}
                  placeholder="https:// (optional)"
                  onChange={(event) => setDraft((current) => ({ ...current, sourceUrl: event.target.value }))}
                />
              </AdminField>

              {sourceNeedsPermission(draft.source) ? (
                <label className="admin-reviews__check admin-reviews__check--permission">
                  <input
                    type="checkbox"
                    checked={draft.permissionConfirmed}
                    onChange={(event) => setDraft((current) => ({ ...current, permissionConfirmed: event.target.checked }))}
                  />
                  <span>
                    <strong>Permission to publish confirmed</strong>
                    <small>Required before Message, Email or Other feedback can be published.</small>
                  </span>
                </label>
              ) : null}
            </section>

            <section className="admin-reviews__editor-section" aria-labelledby="admin-review-section-publication">
              <div className="admin-reviews__section-heading admin-reviews__section-heading--with-state">
                <div>
                  <span>SECTION 3</span>
                  <h3 id="admin-review-section-publication">Publication</h3>
                </div>
                <strong className="admin-reviews__slot-count">{data.homepageSelectedCount} / 3 homepage slots selected</strong>
              </div>

              <div className="admin-reviews__publication-grid">
                <div className="admin-reviews__publication-control">
                  <div>
                    <span className="admin-reviews__control-eyebrow">REVIEWS PAGE</span>
                    <strong>Publish this review</strong>
                  </div>
                  <label className="admin-reviews__toggle">
                    <input
                      type="checkbox"
                      checked={draft.published}
                      aria-label="Publish this review"
                      onChange={(event) => setDraft((current) => ({
                        ...current,
                        published: event.target.checked,
                        homepageFeatured: event.target.checked ? current.homepageFeatured : false,
                        homepageOrder: event.target.checked ? current.homepageOrder : '',
                      }))}
                    />
                    <span aria-hidden="true" />
                  </label>
                </div>

                <div className="admin-reviews__publication-control">
                  <div>
                    <span className="admin-reviews__control-eyebrow">HOMEPAGE</span>
                    <strong>Feature on homepage</strong>
                  </div>
                  <label className="admin-reviews__toggle">
                    <input
                      type="checkbox"
                      checked={draft.homepageFeatured}
                      disabled={!draft.published}
                      aria-label="Feature on homepage"
                      onChange={(event) => setDraft((current) => ({
                        ...current,
                        homepageFeatured: event.target.checked,
                        homepageOrder: event.target.checked ? (current.homepageOrder || '1') : '',
                      }))}
                    />
                    <span aria-hidden="true" />
                  </label>
                </div>
              </div>

              {draft.homepageFeatured ? (
                <div className="admin-reviews__homepage-position">
                  <AdminField label="Homepage position" required hint="Occupied positions are rejected rather than replacing another review.">
                    <AdminSelect
                      value={draft.homepageOrder}
                      onChange={(event) => setDraft((current) => ({ ...current, homepageOrder: event.target.value }))}
                    >
                      <option value="">Choose position</option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                    </AdminSelect>
                  </AdminField>
                </div>
              ) : null}
            </section>

            <section className="admin-reviews__editor-section admin-reviews__editor-section--internal" aria-labelledby="admin-review-section-internal">
              <div className="admin-reviews__section-heading">
                <div>
                  <span>SECTION 4</span>
                  <h3 id="admin-review-section-internal">Internal</h3>
                </div>
              </div>

              <AdminField label="Private admin note">
                <textarea
                  className="admin-ui-input admin-reviews__textarea admin-reviews__textarea--short"
                  value={draft.privateAdminNote}
                  maxLength={1000}
                  rows={3}
                  placeholder="Optional — never shown publicly"
                  onChange={(event) => setDraft((current) => ({ ...current, privateAdminNote: event.target.value }))}
                />
              </AdminField>
            </section>
          </div>
        </AdminModal>

        <AdminConfirmDialog
          open={Boolean(deleteTarget)}
          title="Delete review?"
          description={deleteTarget ? `Delete the review from ${deleteTarget.customerDisplayName}? This permanently removes it from Boss Zone and all public review surfaces. The deletion is still recorded in the audit log.` : 'Delete this review?'}
          confirmLabel={busy ? 'Deleting…' : 'Delete review'}
          dangerous
          onConfirm={() => void deleteReview()}
          onCancel={() => { if (!busy) setDeleteTarget(null) }}
        />

        <AdminToastRegion
          toasts={toasts}
          onDismiss={(id) => setToasts((current) => current.filter((item) => item.id !== id))}
        />
      </div>
    </AdminShell>
  )
}
